import * as Sentry from '@sentry/react';
import forge from 'apis/forge';
import {
	getClipboardItems,
	getFigmaLink,
	getFrameIdsFromText,
	getMoonshotClipboardFromText,
	hasPastableItems,
	pasteFigmaLinkAsFrame,
	pasteImageAsFrame,
	pasteMoonshotAsFrame,
	putTextOnClipboard,
} from 'clipboard';
import * as api from 'dragonmouse-shared/api/forge';
import { getBoundingBox, Rect } from 'dragonmouse-shared/geometry';
import { DesignSystem, Frame, FrameStatus, FrameType, Scene } from 'dragonmouse-shared/interfaces';
import { toast } from 'react-toastify';
import {
	selectActiveDsChat,
	selectActiveScene,
	selectActiveSceneChat,
	selectFrames,
	selectUser,
} from 'redux/db/reducers';
import {
	canEdit,
	canExportSvg,
	designSystemEditingBlocked,
	Direction,
	getAdjacentFrame,
	getFrame,
	getFrameDisplayBounds,
	getFrameExportHtml,
	getFrameImage,
	getFrameSvg,
} from 'redux/db/util';
import {
	centerCanvasAroundSceneBoundingBox,
	setActiveSceneChatId,
	setActiveLeftPanelTab,
	setActiveSceneId,
	setFrameCentered,
	setHovered,
	setHistoricalDsVersionNumber,
	setPanelsVisible,
	setSelected,
	showConfirmModal,
	zoomAroundCanvasCenter,
	zoomSetAroundCanvasCenter,
} from 'redux/mode/actions';
import eventManager from 'events/EventManager';
import { Pick } from 'redux/mode/classes';
import { executeCommand, redo, undo } from 'redux/mode/commandManager';
import {
	DeleteCommand,
	UpdateChatCommand,
	UpdateDsChatCommand,
	UpdateDesignSystemCommand,
	UpdateFrameCommand,
	UpdateSceneCommand,
} from 'redux/mode/commands';
import { LeftPanelTab, ZoomType } from 'redux/mode/reducers';
import { displayPromptModal, getSelectedFrameIds } from 'redux/mode/util';
import { selectSelectedFrames } from 'redux/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import { Target, TargetCanvas } from 'transfer/Target';
import JSZip from 'jszip';
import { buildExportFilename, downloadBlob, ExportFormat, generateShortcutText, navigate } from 'utils';
import { v4 as uuid } from 'uuid';
import {
	trackCreativeAction,
	trackDesignSystemAction,
	trackMiscAction,
	trackOutputAction,
	trackSceneAction,
} from '../analytics';
import { interceptIfNoCredits } from 'economy';
import { displayViewOnlyModal } from '../redux/mode/util';
import { displayDsReadOnlyModal, displayPublishDsModal } from '../redux/mode/util/modals';

export abstract class MenuItem {
	constructor(public readonly kind: string) {}

	// Returns a modified version of this MenuItem, which triggers the provided
	// callback AFTER it is selected. Items without a callback will not be modified.
	// NOTE: i thought about putting this only on MenuItemAction but this feels a bit cleaner, dunno - ss
	addCallback(_callback: () => void): MenuItem {
		return this;
	}
}

export class MenuItemDivider extends MenuItem {
	constructor() {
		super('MenuItemDivider');
	}
}

export class MenuItemAction extends MenuItem {
	constructor(
		public readonly label: string,
		public readonly shortcut: string,
		public readonly callback: () => any,
		public readonly disabled?: boolean,
		public readonly showCheckmark?: boolean
	) {
		super('MenuItemAction');
	}

	addCallback(callback: () => void): MenuItem {
		return new MenuItemAction(
			this.label,
			this.shortcut,
			() => {
				// first call the existing callbacks (this.callback)
				this.callback();
				// then call the new callback
				callback();
			},
			this.disabled,
			this.showCheckmark
		);
	}

	static createUndo(): MenuItemAction {
		return new this(
			'Undo',
			generateShortcutText('Z', { ctrl: true }),
			async () => {
				undo();
			},
			store.getState().mode.commandStack.length === 0
		);
	}

	static createRedo(): MenuItemAction {
		return new this(
			'Redo',
			generateShortcutText('Z', { ctrl: true, shift: true }),
			async () => {
				redo();
			},
			store.getState().mode.undoneCommands.length === 0
		);
	}

	static createZoomIn(): MenuItemAction {
		return new this('Zoom in', generateShortcutText('+', { ctrl: true }), async () => {
			store.dispatch(zoomAroundCanvasCenter(ZoomType.ZOOM_IN));
		});
	}

	static createZoomOut(): MenuItemAction {
		return new this('Zoom out', generateShortcutText('–', { ctrl: true }), async () => {
			store.dispatch(zoomAroundCanvasCenter(ZoomType.ZOOM_OUT));
		});
	}

	static createZoomReset(): MenuItemAction {
		return new this('Zoom to 100%', generateShortcutText('0', { ctrl: true }), async () => {
			store.dispatch(zoomSetAroundCanvasCenter(1.0));
		});
	}

	static createZoomToFitCanvas(): MenuItemAction {
		const frames = selectFrames(store.getState().db);
		return new this(
			'Zoom to fit canvas',
			generateShortcutText('1', { shift: true }),
			async () => {
				const bounds = getBoundingBox(frames.map((frame) => getFrameDisplayBounds(frame)));
				store.dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: bounds,
						animated: true,
					})
				);
			},
			frames.length === 0
		);
	}

	static createZoomToFitSelection(): MenuItemAction {
		return new this(
			'Zoom to fit selection',
			generateShortcutText('3', { shift: true }),
			async () => {
				const selectedFrames = selectSelectedFrames(store.getState());
				const bounds = getBoundingBox(selectedFrames.map((frame) => getFrameDisplayBounds(frame)));
				store.dispatch(
					centerCanvasAroundSceneBoundingBox({
						boundingBox: bounds,
						animated: true,
						tightFit: true,
					})
				);
			},
			getSelectedFrameIds(store.getState().mode.selected).length === 0
		);
	}

	static createDeleteFrames(frameIds: string[]): MenuItemAction {
		return new this('Delete', 'Del', () => {
			const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			trackMiscAction('delete_frame');
			executeCommand(new DeleteCommand(frameIds));
		});
	}

	static createCenterFrame(frame: Frame): MenuItemAction {
		return new this('Focus frame', 'Dbl-click', async () => {
			trackMiscAction('focus_frame');
			store.dispatch(setSelected(new Pick(frame.id)));
			store.dispatch(setHovered(null));
			store.dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: getFrameDisplayBounds(frame),
					animated: true,
					frameId: frame.id,
				})
			);
			store.dispatch(setFrameCentered(frame.id));
		});
	}

	static createNavFromFrame(frameId: string, direction: Direction): MenuItemAction {
		const frame = getAdjacentFrame(frameId, direction);
		return new this(
			`Navigate ${direction.toLowerCase()}`,
			'',
			() => {
				if (frame) {
					trackMiscAction('focus_arrow');
					MenuItemAction.createCenterFrame(frame).callback();
				}
			},
			!frame
		);
	}

	static createCopyFrameUrl(frames: Frame | Frame[]): MenuItemAction {
		return new this('Copy', generateShortcutText('C', { ctrl: true }), async () => {
			trackMiscAction('copy_frame');
			if (Array.isArray(frames)) {
				const urls = frames
					.map(
						(frame) =>
							`${window.location.origin}${routes.modifyCurrentPath({
								frameId: frame.id,
								edit: 'false',
							})}`
					)
					.join('\n');
				putTextOnClipboard(urls);
			} else {
				putTextOnClipboard(
					`${window.location.origin}${routes.modifyCurrentPath({
						frameId: frames.id,
						edit: 'false',
					})}`
				);
			}
		});
	}

	static createCopyFrameImage(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as PNG',
			'',
			async () => {
				trackOutputAction('copy_png');
				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameImage(frame);
							const clipboardItem = new ClipboardItem({
								'image/png': blob,
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy screen:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying PNG...',
					success: 'PNG copied to clipboard',
					error: 'Failed to copy PNG',
				});
			},
			frame.metadata.status !== FrameStatus.READY
		);
	}

	static createCopyFrameSvg(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as Figma SVG',
			'',
			async () => {
				trackOutputAction('copy_svg');
				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameSvg(frame);
							const svgText = await blob.text();
							const clipboardItem = new ClipboardItem({
								'text/plain': new Blob([svgText], { type: 'text/plain' }),
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy screen:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying SVG...',
					success: 'SVG copied to clipboard',
					error: 'Failed to copy SVG',
				});
			},
			frame.metadata.status !== FrameStatus.READY || !canExportSvg([frame])
		);
	}

	static createCopyFrameHtml(frame: Frame): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		return new this(
			'Copy as HTML',
			'',
			async () => {
				trackOutputAction('copy_html');
				// An LLM adviused me thus - given the Safari issues we've had with this code,
				// we may wanna try it - ss
				// In studio/src/menu/MenuItem.ts around lines 417 to 463, remove the console.log
				// debug statements and harden the clipboard copy flow so it works when
				// ClipboardItem is not available: after obtaining htmlText, try to create and
				// write a ClipboardItem inside a try/catch; if ClipboardItem or
				// navigator.clipboard.write fails or is unavailable, fall back to
				// navigator.clipboard.writeText(htmlText) if available, and if that also fails,
				// fall back to a DOM-based copy (create a hidden textarea/input, set its value to
				// htmlText, select it, call document.execCommand('copy'), then remove the
				// element). Only update the user export counters after a successful copy, and
				// ensure all error branches reject so toast.promise can show failure.

				const promiseToCopy = new Promise<void>((resolve, reject) => {
					(async () => {
						try {
							const blob = await getFrameExportHtml(frame);
							const htmlText = await blob.text();
							const clipboardItem = new ClipboardItem({
								'text/html': new Blob([htmlText], { type: 'text/html' }),
								'text/plain': new Blob([htmlText], { type: 'text/plain' }),
							});
							await navigator.clipboard.write([clipboardItem]);
							forge.updateUser({
								update: {
									id: user?.id,
									metadata: {
										numberOfExports: numberOfExports + 1,
										lastExportDate: new Date().toISOString(),
									},
								},
							});
						} catch (error) {
							console.error('Failed to copy HTML:', error);
							throw error;
						}
					})()
						.then(() => resolve())
						.catch((error) => reject(error));
				});
				toast.promise(promiseToCopy, {
					pending: 'Copying HTML...',
					success: 'HTML copied to clipboard',
					error: 'Failed to copy HTML',
				});
			},
			frame.metadata.status !== FrameStatus.READY
		);
	}

	static get downloadConfig(): Record<
		ExportFormat,
		{
			action: 'export_png' | 'export_svg' | 'export_html';
			getBlob: (frame: Frame) => Promise<Blob>;
		}
	> {
		return {
			png: { action: 'export_png', getBlob: getFrameImage },
			svg: { action: 'export_svg', getBlob: getFrameSvg },
			html: { action: 'export_html', getBlob: getFrameExportHtml },
		};
	}

	static createDownloadFrame(frame: Frame, format: ExportFormat): MenuItemAction {
		const user = selectUser(store.getState().db);
		const numberOfExports = user?.metadata.numberOfExports || 0;
		const config = this.downloadConfig[format];
		const label = format.toUpperCase();
		const disabled =
			frame.metadata.status !== FrameStatus.READY || (format === 'svg' && !canExportSvg([frame]));

		return new this(
			label === 'HTML' ? 'Download HTML' : `Download as ${label}`,
			'',
			async () => {
				trackOutputAction(config.action);
				const blobPromise = config.getBlob(frame);
				const filename = `${frame.title || 'untitled'}.${format}`;
				toast.promise(blobPromise, {
					pending: `Exporting ${label}…`,
					success: `Saved ${filename} to your downloads`,
					error: 'Export failed. Please try again.',
				});
				downloadBlob(await blobPromise, filename);
				if (user?.id) {
					forge.updateUser({
						update: {
							id: user.id,
							metadata: {
								numberOfExports: numberOfExports + 1,
								lastExportDate: new Date().toISOString(),
							},
						},
					});
				}
			},
			disabled
		);
	}

	static createDownloadFramesZip(
		frames: Frame[],
		format: ExportFormat,
		sceneTitle?: string | null
	): MenuItemAction {
		const config = this.downloadConfig[format];
		const plural = frames.length === 1 ? '' : 's';
		const label = `Download ${format.toUpperCase()}${plural}`;

		return new this(
			label,
			'',
			async () => {
				const description = `${frames.length} ${format.toUpperCase()}${plural}`;
				const zipFilename = buildExportFilename(sceneTitle, format, `${format}s`);
				const promiseToExport = (async () => {
					const zip = new JSZip();
					const usedFilenames = new Map<string, number>();

					for (const frame of frames) {
						if (format === 'svg' && !canExportSvg([frame])) continue;

						const baseName = frame.title || 'untitled';
						let count = usedFilenames.get(baseName) || 0;
						let filename = count > 0 ? `${baseName} (${count})` : baseName;
						usedFilenames.set(baseName, count + 1);

						trackOutputAction(config.action);
						const blob = await config.getBlob(frame);
						zip.file(`${filename}.${format}`, blob);
					}

					const zipBlob = await zip.generateAsync({ type: 'blob' });
					downloadBlob(zipBlob, zipFilename);
				})();

				toast.promise(promiseToExport, {
					pending: `Exporting ${description}…`,
					success: `Saved ${zipFilename} to your downloads`,
					error: 'Export failed. Please try again.',
				});
				await promiseToExport;
			},
			frames.some((f) => f.metadata.status !== FrameStatus.READY)
		);
	}

	static createRetry(frame: Frame): MenuItemAction {
		// Currently, GENERATE_FROM_CHAT frames created after Nov 2025 have recipes and can be retried
		// Older GENERATE_FROM_CHAT frames don't have recipes; they will fail on the backend if retry is attempted
		//
		// Originally we did a check for a recipe, but the forge is no longer sending recipe entries over because they are a lot of data
		// So for now let's check the frame type. Older frames will fail on the backend. I think we can live with that
		// (in practice only recently generated frames tend to be retried)
		const canRetry = frame.metadata.type === FrameType.GENERATE_FROM_CHAT;

		return new this(
			'Retry generation',
			'',
			async () => {
				const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
				if (!canEdit(scene)) {
					displayViewOnlyModal();
					return;
				}
				if (interceptIfNoCredits({ kind: 'chatEdit', sceneId: frame.sceneId })) {
					return;
				}
				trackCreativeAction('retry');
				try {
					const response = await forge.retryFrame.withToast()({ frameId: frame.id });
					if (!response.success) {
						toast.info('Frame cannot be retried');
					}
				} catch (e) {
					toast.error('Frame cannot be retried');
				}
			},
			!canRetry
		);
	}

	static async createPaste(target: Target): Promise<MenuItemAction> {
		let complete: (() => void) | null = null;

		const sceneId = store.getState().mode.activeSceneId;
		const clipboardItems = await getClipboardItems();
		if (target instanceof TargetCanvas && sceneId && (await hasPastableItems(clipboardItems))) {
			complete = async () => {
				try {
					for (const item of clipboardItems) {
						for (const type of item.types) {
							if (type === 'image/png') {
								trackMiscAction('paste_image');
								pasteImageAsFrame(item, sceneId, target.scene);
							} else if (type === 'text/plain') {
								const text = await item.getType('text/plain');
								const textContent = await text.text();

								const figmaLink = getFigmaLink(textContent);
								const frameIds = getFrameIdsFromText(textContent);
								const moonshotClipboard = getMoonshotClipboardFromText(textContent);

								if (figmaLink) {
									trackMiscAction('paste_figma_link');
									pasteFigmaLinkAsFrame(figmaLink, target.scene);
								} else if (frameIds.length > 0) {
									trackMiscAction('paste_frames');
									// Get all the frames to duplicate
									const frames = frameIds
										.map((id) => getFrame(id))
										.filter((f): f is Frame => f !== null);

									// If all the frames are found locally, use this duplicateFrame
									// which gives us nice optimistic updates.
									if (frames.length === frameIds.length) {
										const allBounds = frames.map(getFrameDisplayBounds);
										const minX = Math.min(...allBounds.map((b) => b.x));
										const minY = Math.min(...allBounds.map((b) => b.y));

										const duplicationTasks = frames.map((frame) => {
											const newFrameId = uuid();
											const frameBounds = getFrameDisplayBounds(frame);
											const relativeX = frameBounds.x - minX;
											const relativeY = frameBounds.y - minY;
											return {
												frame,
												newFrameId,
												x: Math.round(target.scene.x + (frames.length === 1 ? 0 : relativeX)),
												y: Math.round(target.scene.y + (frames.length === 1 ? 0 : relativeY)),
											};
										});

										await Promise.all(
											duplicationTasks.map(({ frame, newFrameId, x, y }) =>
												forge.duplicateFrame(frame, newFrameId, sceneId, x, y)
											)
										);
									} else {
										// Some frames are missing - this means we are either pasting in frames from a different scene
										// or pasting frames that were deleted
										const response: api.PasteFramesIntoSceneResponse =
											await forge.pasteFramesIntoScene({
												sceneId,
												frameIds,
												x: target.scene.x,
												y: target.scene.y,
											});

										if (response.skippedFrameIds.length > 0) {
											toast.warn(
												`Could not paste ${response.skippedFrameIds.length} frame(s) - access denied`
											);
										}
									}
								} else if (moonshotClipboard) {
									trackMiscAction('paste_moonshot');
									pasteMoonshotAsFrame(moonshotClipboard, sceneId, target.scene);
								}
							}
						}
					}
				} catch (error) {
					console.error('Failed to read clipboard contents:', error);
				}
			};
		}

		return new this(
			'Paste',
			generateShortcutText('v', { ctrl: true }),
			() => {
				complete?.();
			},
			!complete
		);
	}

	static async createPasteText(onPaste: (text: string) => void): Promise<MenuItemAction> {
		const clipboardItems = await getClipboardItems();
		const textItem = clipboardItems.find((item) => item.types.includes('text/plain'));
		let text = '';
		if (textItem) {
			const textType = await textItem.getType('text/plain');
			text = await textType.text();
		}
		return new this(
			'Paste',
			generateShortcutText('v', { ctrl: true }),
			() => {
				onPaste(text);
			},
			!text.length
		);
	}

	static createCopyText(text: string): MenuItemAction {
		return new this('Copy', generateShortcutText('c', { ctrl: true }), async () => {
			putTextOnClipboard(text);
		});
	}

	static createRenameScene(scene: Scene): MenuItemAction {
		return new this('Rename project...', '', () => {
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			displayPromptModal({
				title: 'Rename project',
				description: 'Enter a new name for the project',
				submitLabel: 'Rename',
				initialValue: scene.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackSceneAction('rename');
					executeCommand(
						new UpdateSceneCommand(
							{ id: scene.id, title: scene.title },
							{ id: scene.id, title: value }
						)
					);
				},
			});
		});
	}

	static createRenameDesignSystem(designSystem: DesignSystem): MenuItemAction {
		return new this('Rename design system...', '', () => {
			const blocked = designSystemEditingBlocked();
			if (blocked) {
				displayDsReadOnlyModal(blocked);
				return;
			}
			displayPromptModal({
				title: `Rename "${designSystem.title}"`,
				description: 'Enter a new name for the design system',
				submitLabel: 'Rename',
				initialValue: designSystem.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackDesignSystemAction('rename');
					executeCommand(
						new UpdateDesignSystemCommand(
							{ id: designSystem.id, title: designSystem.title },
							{ id: designSystem.id, title: value }
						)
					);
				},
			});
		});
	}

	static createRenameFrame(frame: Frame): MenuItemAction {
		return new this('Rename frame...', '', async () => {
			const scene = selectActiveScene(store.getState().db, store.getState().mode.activeSceneId);
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			displayPromptModal({
				title: 'Rename frame',
				description: 'Enter a new name for the frame',
				submitLabel: 'Rename',
				initialValue: frame.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackMiscAction('rename_frame');
					executeCommand(
						new UpdateFrameCommand(
							{ id: frame.id, title: frame.title },
							{ id: frame.id, title: value }
						)
					);
				},
			});
		});
	}

	static createReturnToProjectList(): MenuItemAction {
		return new this('Return to project list...', '', () => {
			trackSceneAction('return_to_scene_list');
			// there is a useEffect in App.tsx that navigates to the project page whenever activeSceneId is set
			// this useEffect ends up navigating us back to the project when we try to navigate to the home page!
			// so i clear the activeSceneId first. This is a quick hack that works for now.
			// Later I'd like to clean this up. Too many parties are responsible for navigating.
			store.dispatch(setActiveSceneId(null));
			navigate(routes.home());
		});
	}

	static createRenameChat(chatId: string): MenuItemAction {
		return new this('Rename chat...', '', async () => {
			const state = store.getState();
			const scene = selectActiveScene(state.db, state.mode.activeSceneId);
			if (!canEdit(scene)) {
				displayViewOnlyModal();
				return;
			}
			const chat = selectActiveSceneChat(state.db, chatId);
			if (!chat) {
				toast.error('Could not find chat to rename');
				return;
			}

			displayPromptModal({
				title: 'Rename chat',
				description: 'Enter a new name for the chat',
				submitLabel: 'Rename',
				initialValue: chat.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackMiscAction('rename_chat');
					executeCommand(
						new UpdateChatCommand({ id: chatId, title: chat.title }, { id: chatId, title: value })
					);
				},
			});
		});
	}

	static createRenameDsChat(chatId: string): MenuItemAction {
		return new this('Rename chat...', '', async () => {
			const blocked = designSystemEditingBlocked();
			if (blocked) {
				displayDsReadOnlyModal(blocked);
				return;
			}
			const state = store.getState();
			const chat = selectActiveDsChat(state.db, chatId);
			if (!chat) {
				toast.error('Could not find chat to rename');
				return;
			}

			displayPromptModal({
				title: 'Rename chat',
				description: 'Enter a new name for the chat',
				submitLabel: 'Rename',
				initialValue: chat.title,
				onSubmit: async (value?: string) => {
					if (!value) return;
					trackMiscAction('rename_ds_chat');
					executeCommand(
						new UpdateDsChatCommand({ id: chatId, title: chat.title }, { id: chatId, title: value })
					);
				},
			});
		});
	}

	static createCopyChatId(chatId: string): MenuItemAction {
		return new this('Copy chat ID', '', async () => {
			try {
				await putTextOnClipboard(chatId);
				toast.success('Chat ID copied');
			} catch (error) {
				toast.error('Copy failed — please copy manually');
				console.error('Failed to copy chat ID to clipboard', error);
			}
		});
	}

	static createCopyFrameId(frameId: string): MenuItemAction {
		return new this('Copy frame ID', '', async () => {
			try {
				await putTextOnClipboard(frameId);
				toast.success('Frame ID copied');
			} catch (error) {
				toast.error('Copy failed — please copy manually');
				Sentry.captureException(error);
			}
		});
	}

	static createSelectFramesFromChat(chatId: string): MenuItemAction {
		return new this('Select frames from chat', '', async () => {
			// gather frames which (1) have chatId in their metadata.sourceChatId and (2) are not deleted
			const frames = selectFrames(store.getState().db).filter(
				(frame) => frame.metadata.sourceChatId === chatId
			);

			if (frames.length === 0) {
				toast.warning('No frames in this chat are on the canvas');
				return;
			}

			// get the bounds for these frames and zoom to fit
			const bounds: Rect[] = [];
			for (const frame of frames) {
				bounds.push(getFrameDisplayBounds(frame));
			}
			store.dispatch(
				centerCanvasAroundSceneBoundingBox({
					boundingBox: getBoundingBox(bounds),
					animated: true,
				})
			);

			store.dispatch(setSelected(frames.map((frame) => new Pick(frame.id))));
		});
	}

	static createScrollToFrameInChat(chatId: string, frameId?: string): MenuItemAction {
		return new this('Show in chat', '', async () => {
			const activeLeftPanelTab = store.getState().mode.activeLeftPanelTab;
			const panelsVisible = store.getState().mode.panelsVisible;

			// activate the chat panel if needed
			if (activeLeftPanelTab !== LeftPanelTab.CHAT) {
				store.dispatch(setActiveLeftPanelTab(LeftPanelTab.CHAT));
			}
			if (!panelsVisible) {
				store.dispatch(setPanelsVisible(true));
			}
			store.dispatch(setActiveSceneChatId(chatId));

			if (frameId) {
				eventManager.emit('scroll-chat-to-frame', frameId);
			}
		});
	}

	static createOpenPrototype(frameId: string): MenuItemAction {
		const frame = getFrame(frameId);
		return new this(
			'Preview',
			'P',
			() => {
				trackMiscAction('load_view_prototype');
				navigate(routes.modifyCurrentPath({ frameId }));
			},
			!frame
		);
	}

	static createOpenPrototypeInNewTab(frame: Frame): MenuItemAction {
		return new this('Preview in new tab', '', async () => {
			trackMiscAction('load_prototype_in_new_tab');
			window.open(
				`${window.location.origin}${routes.modifyCurrentPath({ frameId: frame.id })}`,
				'_blank'
			);
		});
	}

	static createPublishDs(dsId: string, dsTitle: string): MenuItemAction {
		return new this('Publish', '', () => {
			const blocked = designSystemEditingBlocked();
			if (blocked) {
				displayDsReadOnlyModal(blocked);
				return;
			}
			if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: dsId })) return;
			displayPublishDsModal(dsId, dsTitle);
		});
	}

	static createDiscardDsChanges(dsId: string, dsTitle: string): MenuItemAction {
		return new this('Discard Changes', '', () => {
			const blocked = designSystemEditingBlocked();
			if (blocked) {
				displayDsReadOnlyModal(blocked);
				return;
			}
			store.dispatch(
				showConfirmModal({
					title: `Discard changes to ${dsTitle}?`,
					description:
						'All draft changes will be lost and the design system will revert to the last published version. This cannot be undone.',
					cancelLabel: 'Cancel',
					submitLabel: 'Discard Changes',
					onSubmit: async () => {
						try {
							await forge.discardDsVersion(dsId);
							trackDesignSystemAction('revert');
							toast.success('Draft changes discarded');
						} catch {
							toast.error('Failed to discard changes');
						}
					},
				})
			);
		});
	}

	static createRestoreVersion(dsId: string, vNum: number, dsHasDraft: boolean): MenuItemAction {
		return new this('Restore this version', '', () => {
			const blocked = designSystemEditingBlocked();
			if (blocked && blocked !== 'historical') {
				displayDsReadOnlyModal(blocked);
				return;
			}
			const description = dsHasDraft
				? `Your current draft changes will be discarded. v${vNum} will be restored as the latest version.`
				: `v${vNum} will be restored as the latest version.`;
			store.dispatch(
				showConfirmModal({
					title: `Restore v${vNum}?`,
					description,
					cancelLabel: 'Cancel',
					submitLabel: 'Restore',
					onSubmit: async () => {
						try {
							await forge.restoreDesignSystemVersion({
								designSystemId: dsId,
								sourceVersionNumber: vNum,
							});
							trackDesignSystemAction('restore_version', { version: vNum });
							store.dispatch(setHistoricalDsVersionNumber(null));
							toast.success(`Restored v${vNum} as the latest version`);
						} catch {
							toast.error('Failed to restore version');
						}
					},
				})
			);
		});
	}
}
